// src/utils/bankCategorizationRules.js - Modificado para considerar a configuração de categorização automática

/**
 * Regras de categorização para diferentes bancos
 * Este arquivo contém as regras específicas para categorização automática
 * de transações de diferentes bancos
 */

import { extractBoletoInfo } from './patternRules';

/**
 * Verifica se estamos usando uma conta pessoal
 * @returns {boolean} - True se a conta ativa for do tipo pessoal
 */
const isPersonalAccount = () => {
  try {
    return localStorage.getItem('active_account_type') === 'personal';
  } catch (error) {
    console.error('Erro ao verificar tipo de conta:', error);
    return false;
  }
};

/**
 * Verifica se estamos usando uma conta empresarial
 * @returns {boolean} - True se a conta ativa for do tipo empresarial
 */
const isBusinessAccount = () => {
  try {
    return localStorage.getItem('active_account_type') === 'business';
  } catch (error) {
    console.error('Erro ao verificar tipo de conta:', error);
    return true; // Por padrão, assumimos conta empresarial
  }
};

/**
 * Verifica se a categorização automática para bancos está ativada
 * @returns {boolean} - True se a categorização automática estiver ativada
 */
const isAutomaticBankCategorizationEnabled = () => {
  try {
    return localStorage.getItem('automaticBankCategorizationOFX') !== 'false';
  } catch (error) {
    console.error('Erro ao verificar configuração de categorização automática:', error);
    return true; // Por padrão, está ativada
  }
};

/**
 * Aplica regras de categorização para o banco SICOOB/COOP
 * @param {Object} transaction - Dados da transação
 * @returns {Object} - Informações de categoria, se aplicável
 */
const categorizeSicoobTransactions = (transaction) => {
  const { description, amount } = transaction;
  
  // Verifica se estamos em uma conta pessoal e o valor é positivo
  if (isPersonalAccount() && amount > 0) {
    return {
      category: "Receitas",
      categoryPath: "💸 Receitas.Receitas",
      autoMapped: true,
      positiveOnly: true,
      originalAmount: amount
    };
  }
  
  // NOVO: Verifica se estamos em uma conta empresarial, com valor positivo, 
  // e a categorização automática está desativada
  if (isBusinessAccount() && amount > 0 && !isAutomaticBankCategorizationEnabled()) {
    return {
      category: "Receitas",
      categoryPath: "RECEITA.Receitas",
      autoMapped: true,
      positiveOnly: true,
      originalAmount: amount
    };
  }
  
  // Primeiro verificar se é um boleto e extrair o beneficiário
  const boletoInfo = extractBoletoInfo(description);
  if (boletoInfo && boletoInfo.beneficiary) {
    // Para boletos, não categorizar automaticamente
    // Isso permite que o sistema de categorização por beneficiário atue
    return null;
  }
  
  // Regras para transações positivas - RECEITA
  if (amount > 0) {
    // REGRAS DE PRIORIDADE MÁXIMA - Verificar primeiro sem importar outras palavras-chave
    
    // Verificar se contém "ALELO" (regra de máxima prioridade)
    if (description.toUpperCase().includes('ALELO')) {
      return {
        category: 'Voucher',
        categoryPath: 'RECEITA.Voucher',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    
    // Verificar se contém "Banco VR" (regra de máxima prioridade)
    if (description.toUpperCase().includes('BANCO VR')) {
      return {
        category: 'Voucher',
        categoryPath: 'RECEITA.Voucher',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    
    // REGRAS REGULARES - Verificar em ordem normal
    
    // Nova regra: verificar se contém "PIX-PIX" em qualquer lugar
    if (description.toUpperCase().includes('PIX-PIX')) {
      return {
        category: 'Pix',
        categoryPath: 'RECEITA.Pix',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    
    // Primeira regra regular: verificar se contém "IFOOD" (insensitive)
    if (description.toUpperCase().includes('IFOOD')) {
      return {
        category: 'Ifood',
        categoryPath: 'RECEITA.Ifood',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    
    // Nova regra: verificar se contém "DEBITO" em qualquer lugar para valor positivo
    if (description.toUpperCase().includes('DEBITO')) {
      return {
        category: 'Cartão de Débito',
        categoryPath: 'RECEITA.Cartão de Débito',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    
    // Modificado: verificar débito de forma genérica, ignorando bandeiras
    const upperDesc = description.toUpperCase();
    if ((upperDesc.includes('DEB') || upperDesc.includes('DB')) && 
        !(upperDesc.includes('ANTEC') || upperDesc.includes('CR'))) {
      return {
        category: 'Cartão de Débito',
        categoryPath: 'RECEITA.Cartão de Débito',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    } 
    // Modificado: verificar crédito de forma genérica, ignorando bandeiras
    else if (upperDesc.includes('ANTEC') || upperDesc.includes('CR')) {
      return {
        category: 'Cartão de Crédito',
        categoryPath: 'RECEITA.Cartão de Crédito',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    } 
    else if (upperDesc.includes('PIX')) {
      return {
        category: 'Pix',
        categoryPath: 'RECEITA.Pix',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    } 
    else if (upperDesc.includes('TED')) {
      return {
        category: 'TED',
        categoryPath: 'RECEITA.TED',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
  } 
  // Regras para transações negativas - DESPESAS
  else {
    // Aqui podemos adicionar regras específicas para categorizar despesas
    // Por enquanto, apenas retornamos null para não categorizar automaticamente
    // Permitindo que o sistema de categorização por beneficiário funcione
    return null;
  }
  
  return null;
};

/**
 * Aplica regras de categorização para o banco Stone
 * @param {Object} transaction - Dados da transação
 * @returns {Object} - Informações de categoria, se aplicável
 */
const categorizeStoneTransactions = (transaction) => {
  const { description, amount } = transaction;
  
  // Verifica se estamos em uma conta pessoal e o valor é positivo
  if (isPersonalAccount() && amount > 0) {
    return {
      category: "Receitas",
      categoryPath: "💸 Receitas.Receitas",
      autoMapped: true,
      positiveOnly: true,
      originalAmount: amount
    };
  }
  
  // NOVO: Verifica se estamos em uma conta empresarial, com valor positivo, 
  // e a categorização automática está desativada
  if (isBusinessAccount() && amount > 0 && !isAutomaticBankCategorizationEnabled()) {
    return {
      category: "Receitas",
      categoryPath: "RECEITA.Receitas",
      autoMapped: true,
      positiveOnly: true,
      originalAmount: amount
    };
  }
  
  // Verificar se é um boleto e extrair o beneficiário
  const boletoInfo = extractBoletoInfo(description);
  if (boletoInfo && boletoInfo.beneficiary) {
    // Para boletos, não categorizar automaticamente
    // Isso permite que o sistema de categorização por beneficiário atue
    return null;
  }
  
  // Apenas categorizar transações positivas para RECEITA
  if (amount > 0) {
    // Regra prioritária: verificar se contém "ifood" (insensitive)
    if (description.toLowerCase().includes('ifood')) {
      return {
        category: 'Ifood',
        categoryPath: 'RECEITA.Ifood',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    
    // Nova regra: Verificar se contém "Recebimento vendas - Antecipação"
    if (description.includes('Recebimento vendas - Antecipação')) {
      return {
        category: 'Cartão de Crédito',
        categoryPath: 'RECEITA.Cartão de Crédito',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    // Verifica se termina com "Maquininha"
    else if (description.trim().endsWith('Maquininha')) {
      return {
        category: 'Pix',
        categoryPath: 'RECEITA.Pix',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    // Verifica se contém "Crédito"
    else if (description.includes('Crédito')) {
      return {
        category: 'Cartão de Crédito',
        categoryPath: 'RECEITA.Cartão de Crédito',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    // Verifica se termina com "TED"
    else if (description.trim().endsWith('TED')) {
      return {
        category: 'TED',
        categoryPath: 'RECEITA.TED',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    // Verifica se termina com "Débito"
    else if (description.trim().endsWith('Débito')) {
      return {
        category: 'Cartão de Débito',
        categoryPath: 'RECEITA.Cartão de Débito',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    // Verifica se a primeira palavra é "VR"
    else if (description.trim().split(' ')[0] === 'VR') {
      return {
        category: 'Voucher',
        categoryPath: 'RECEITA.Voucher',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
    // Verifica se começa com "ALELO"
    else if (description.trim().startsWith('ALELO')) {
      return {
        category: 'Voucher',
        categoryPath: 'RECEITA.Voucher',
        autoMapped: true,
        positiveOnly: true,
        originalAmount: amount
      };
    }
  }
  // Para transações negativas
  else {
    // Adicionar regras para despesas aqui, se necessário
    return null;
  }
  
  return null;
};

/**
 * Categoriza uma transação baseado nas regras específicas de cada banco
 * @param {Object} transaction - Dados da transação
 * @param {Object} bankInfo - Informações do banco
 * @returns {Object} - Informações de categoria, ou null se nenhuma regra aplicável
 */
export const categorizeTransaction = (transaction, bankInfo) => {
  if (!bankInfo || !bankInfo.org) return null;
  
  // Verifica se estamos em uma conta pessoal e o valor é positivo
  // Esta regra tem prioridade sobre todas as outras regras
  if (isPersonalAccount() && transaction.amount > 0) {
    return {
      category: "Receitas",
      categoryPath: "💸 Receitas.Receitas",
      autoMapped: true,
      positiveOnly: true,
      originalAmount: transaction.amount
    };
  }
  
  // NOVO: Verifica se estamos em uma conta empresarial, com valor positivo, 
  // e a categorização automática está desativada
  if (isBusinessAccount() && transaction.amount > 0 && !isAutomaticBankCategorizationEnabled()) {
    return {
      category: "Receitas",
      categoryPath: "RECEITA.Receitas",
      autoMapped: true,
      positiveOnly: true,
      originalAmount: transaction.amount
    };
  }
  
  const bankName = bankInfo.org;
  
  // SICOOB/COOP
  if (bankName.includes('COOP DE CRED') || bankName.includes('SICOOB')) {
    return categorizeSicoobTransactions(transaction);
  }
  
  // Stone
  if (bankName.includes('Stone Instituição de Pagamento')) {
    return categorizeStoneTransactions(transaction);
  }
  
  // Se for conta empresarial com valor positivo e chegou até aqui, usar categoria padrão
  if (isBusinessAccount() && transaction.amount > 0) {
    return {
      category: "Receitas",
      categoryPath: "RECEITA.Receitas",
      autoMapped: true,
      positiveOnly: true,
      originalAmount: transaction.amount
    };
  }
  
  // Adicionar regras para outros bancos aqui
  
  return null;
};